import { useMemo } from "react";
import { reportingPeriod } from "@shared/analyticsReporting";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "./DashboardWidget";
import { getKpiConfig } from "./widgetRegistry";
import { percentChange, formatNumber, formatCurrency, formatPercent } from "@/lib/formatters";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PlatformBadge } from "./PlatformBadge";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";

interface KpiMetricCardProps {
  widgetId: string;
  gridH?: number;
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}


interface PlatformBreakdownRow {
  platformSlug: string;
  platformName: string;
  platformColor: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  conversionValue: number;
  roas: number;
  ctr: number;
}

function getPlatformMetricValue(row: PlatformBreakdownRow, summaryField: string): number {
  const mapping: Record<string, () => number> = {
    spend: () => row.spend,
    impressions: () => row.impressions,
    clicks: () => row.clicks,
    purchases: () => row.conversions,
    purchaseValue: () => row.conversionValue,
    roas: () => row.roas,
    ctr: () => row.ctr,
    cpc: () => row.clicks > 0 ? row.spend / row.clicks : 0,
    cpm: () => row.impressions > 0 ? (row.spend / row.impressions) * 1000 : 0,
    cpp: () => row.conversions > 0 ? row.spend / row.conversions : 0,
  };
  return mapping[summaryField]?.() ?? 0;
}

export function KpiMetricCard({ widgetId, gridH, isEditMode, onRemove, onResize, onResetSize }: KpiMetricCardProps) {
  const config = getKpiConfig(widgetId);
  const { startDate, endDate, comparisonStartDate } = useDashboardDate();
  const { getUrl, dataKey, platformSlugs } = useDashboardDataSource();
  const isMultiPlatform = (platformSlugs?.length ?? 0) > 1 || (platformSlugs?.length ?? 0) === 0;

  const extendedStart = comparisonStartDate < startDate ? comparisonStartDate : startDate;

  const { data: platformBreakdown } = useQuery({
    queryKey: ["dashboard-platform-breakdown", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("platform-breakdown", params));
      if (!res.ok) return [];
      return res.json() as Promise<PlatformBreakdownRow[]>;
    },
    enabled: isMultiPlatform,
  });

  const { data: allTrendData, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-daily-trend", dataKey, extendedStart, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate: extendedStart, endDate });
      const res = await fetch(getUrl("daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch trend data");
      return res.json() as Promise<Array<Record<string, any> & { date: string; reported?: boolean }>>;
    },
  });

  const { currentSummary, previousSummary, sparklineData, comparable } = useMemo(() => {
    if (!allTrendData || !config) return { currentSummary: null, previousSummary: null, sparklineData: null, comparable: false };

    const currentRows = allTrendData.filter(d => d.date >= startDate && d.date <= endDate);
    const previousRows = allTrendData.filter(d => d.date >= comparisonStartDate && d.date < startDate);

    const current = reportingPeriod(currentRows, startDate, endDate);
    const previous = reportingPeriod(previousRows, comparisonStartDate,
      new Date(Date.parse(startDate) - 86400000).toISOString().slice(0, 10));
    const cur = current.summary;
    const prev = previous.summary;

    let spark: Array<{ v: number | null }> | null = null;
    if (currentRows.length > 0) {
      const last7 = currentRows.slice(-7);
      if (config.sparklineCompute) {
        spark = last7.map(d => ({ v: (d as Record<string, unknown>).reported === false || cur?.[config.summaryField] == null ? null : config.sparklineCompute!(d) }));
      } else if (config.trendField) {
        spark = last7.map(d => ({ v: (d[config.trendField!] as number) ?? null }));
      }
    }

    return { currentSummary: cur, previousSummary: prev, sparklineData: spark, comparable: current.complete && previous.complete };
  }, [allTrendData, startDate, endDate, comparisonStartDate, config]);

  if (!config) return null;

  const currentValue = currentSummary?.[config.summaryField] ?? null;
  const previousValue = previousSummary?.[config.summaryField as keyof typeof previousSummary] ?? null;
  const change = comparable && currentValue !== null && previousValue !== null ? percentChange(Number(currentValue), Number(previousValue)) : null;
  const isPositive = change !== null && change > 0;
  const isNegative = change !== null && change < 0;
  const displayedChange = config.invertColors
    ? (isPositive ? "negative" : isNegative ? "positive" : "neutral")
    : (isPositive ? "positive" : isNegative ? "negative" : "neutral");

  const isTall = (gridH ?? 3) >= 4;

  const platformTooltipRows = useMemo(() => {
    if (!isMultiPlatform || !platformBreakdown || platformBreakdown.length <= 1 || !config) return null;
    return platformBreakdown.map(row => ({
      slug: row.platformSlug,
      name: row.platformName,
      color: row.platformColor,
      value: config.formatter(getPlatformMetricValue(row, config.summaryField)),
    }));
  }, [isMultiPlatform, platformBreakdown, config]);

  const valueEl = (
    <p className="text-2xl font-bold truncate" style={{ color: "#1A1A1A" }}>{currentValue === null ? "—" : config?.formatter(currentValue)}</p>
  );

  const wrappedValue = platformTooltipRows ? (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help border-b border-dashed border-gray-300 inline-block">
            {valueEl}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="p-3 max-w-[200px]">
          <p className="text-xs font-semibold text-gray-500 mb-2">By Platform</p>
          <div className="space-y-1.5">
            {platformTooltipRows.map(row => (
              <div key={row.slug} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <PlatformBadge platform={row.slug} size="xs" variant="muted" />
                  <span className="text-xs text-gray-700">{row.name}</span>
                </div>
                <span className="text-xs font-medium text-gray-900">{row.value}</span>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : valueEl;

  return (
    <DashboardWidget
      title={config.title}
      widgetType="kpi"
      isEditMode={isEditMode}
      isLoading={isLoading}
      error={error?.message ?? null}
      onRetry={() => refetch()}
      onRemove={onRemove}
      onResize={onResize}
      onResetSize={onResetSize}
    >
      <div className="px-4 py-3 flex flex-col h-full" data-testid={`kpi-card-${widgetId}`}>
        {isTall ? (
          <>
            <div className="min-w-0">
              {wrappedValue}
              {change !== null && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                    displayedChange === "positive" ? "bg-emerald-50 text-emerald-700" :
                    displayedChange === "negative" ? "bg-red-50 text-red-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {displayedChange === "positive" ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : displayedChange === "negative" ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : (
                      <Minus className="h-3 w-3" />
                    )}
                    {Math.abs(change).toFixed(1)}%
                  </span>
                  <span className="text-[10px] text-gray-400">vs prev</span>
                </div>
              )}
            </div>
            {sparklineData && sparklineData.length > 1 && (
              <div className="flex-1 mt-2 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData}>
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke="var(--analytics-chart-primary, #7b1f40)"
                      strokeWidth={1.5}
                      fill="var(--analytics-chart-fill, #f5e8ee)"
                      fillOpacity={0.3}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              {wrappedValue}
              {change !== null && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
                    displayedChange === "positive" ? "bg-emerald-50 text-emerald-700" :
                    displayedChange === "negative" ? "bg-red-50 text-red-700" :
                    "bg-gray-100 text-gray-500"
                  }`}>
                    {displayedChange === "positive" ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : displayedChange === "negative" ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : (
                      <Minus className="h-3 w-3" />
                    )}
                    {Math.abs(change).toFixed(1)}%
                  </span>
                  <span className="text-[10px] text-gray-400">vs prev</span>
                </div>
              )}
            </div>
            {sparklineData && sparklineData.length > 1 && (
              <div className="w-16 h-8 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparklineData}>
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke="var(--analytics-chart-primary, #7b1f40)"
                      strokeWidth={1.5}
                      fill="var(--analytics-chart-fill, #f5e8ee)"
                      fillOpacity={0.3}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardWidget>
  );
}
